import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  X,
  ArrowLeft,
  Mail,
  CalendarDays,
  ChevronDown,
  Clock,
  Users,
  Mic,
  Settings,
  Shield,
  Link2,
  MousePointer,
  DoorOpen,
  Image,
  Save,
  Check,
} from "lucide-react";
import { toast } from "react-toastify";
import {
  getMeetingRoomById,
  getMeetingRooms,
  getScheduledMeetingById,
  updateScheduledMeeting,
} from "../../../../services/roomServices";
import userService from "../../../../services/userServices";
import { useSiteContext } from "../../../../context/SiteContext";
import Calendar from "../../../calendar";
import "../../../calendar/Calendar.css";
import { TIMEZONES } from "../../../../utils/timezones";

function EditScheduleMeeting() {
  const { user } = useSiteContext();
  const { roomId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [meetingName, setMeetingName] = useState("");
  const [meetingStartTime, setMeetingStartTime] = useState("");
  const [meetingEndTime, setMeetingEndTime] = useState("");
  const [invitesEmailInput, setInvitesEmailInput] = useState("");
  const [invitesList, setInvitesList] = useState([]);

  // Date/time picker state
  const [startDate, setStartDate] = useState("");
  const [startHour, setStartHour] = useState("1");
  const [startMinute, setStartMinute] = useState("30");
  const [startAMPM, setStartAMPM] = useState("AM");
  const [duration, setDuration] = useState("60");
  const [showStartCalendar, setShowStartCalendar] = useState(false);
  const [startDateObj, setStartDateObj] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const timezones = TIMEZONES;

  // Calculate durationHours and durationMinutes from duration
  const parsedDuration = parseInt(duration, 10);
  const normalizedDuration = Number.isNaN(parsedDuration) ? 0 : parsedDuration;
  const durationHours = Math.floor(normalizedDuration / 60);
  const durationMinutes = normalizedDuration % 60;

  // Time options for dropdown (15-minute intervals)
  const TIME_OPTIONS = (() => {
    const minutes = ["00", "15", "30", "45"];
    const options = [];
    for (let hour = 1; hour <= 12; hour += 1) {
      minutes.forEach((minute) => {
        options.push({
          value: `${hour}-${minute}`,
          label: `${hour}:${minute}`,
        });
      });
    }
    return options;
  })();

  // Initialize timezone from system timezone
  const getInitialTimezone = () => {
    const systemTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const matchingTz = TIMEZONES.find(
      (tz) => tz.name === systemTz || tz.zone === systemTz
    );
    return matchingTz ? matchingTz.value : "America/New_York";
  };
  const [selectedTimezone, setSelectedTimezone] = useState(
    getInitialTimezone()
  );

  // Co-Host state
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const [suggestions, setSuggestions] = useState([]);
  const [checkingUser, setCheckingUser] = useState(false);
  const [userExists, setUserExists] = useState(null);
  const [inviteEmails, setInviteEmails] = useState([]);

  // Invitees state — structured list of {email, name} for personalized invite emails
  const [inviteesList, setInviteesList] = useState([]); // [{email, name}]
  const [newInviteeEmail, setNewInviteeEmail] = useState("");
  const [newInviteeName, setNewInviteeName] = useState("");

  // Common email domains for suggestions
  const commonDomains = [
    "gmail.com",
    "yahoo.com",
    "outlook.com",
    "hotmail.com",
    "icloud.com",
    "aol.com",
    "mail.com",
    "protonmail.com",
  ];

  const [roomSettings, setRoomSettings] = useState({
    allowChat: true,
    allowScreenSharing: false,
    muteParticipantsOnEntry: false,
    allowReactions: true,
    allowRaiseHand: true,
    requirePassword: false,
    password: "",
    waitingRoom: false,
  });

  const [exitUrlEnabled, setExitUrlEnabled] = useState(false);
  const [exitUrl, setExitUrl] = useState("");

  const [customButtonEnabled, setCustomButtonEnabled] = useState(false);
  const [customButtonUrl, setCustomButtonUrl] = useState("");
  const [customButtonText, setCustomButtonText] = useState("");

  const [waitingRoomTextEnabled, setWaitingRoomTextEnabled] = useState(false);
  const [waitingRoomTitle, setWaitingRoomTitle] = useState("");
  const [waitingRoomMessage, setWaitingRoomMessage] = useState("");
  const [waitingRoomSubText, setWaitingRoomSubText] = useState("");

  const [customBackgroundEnabled, setCustomBackgroundEnabled] = useState(false);
  const [backgroundType, setBackgroundType] = useState("solid");
  const [backgroundColor, setBackgroundColor] = useState("#1a1a2e");
  const [gradientColor1, setGradientColor1] = useState("#1a1a2e");
  const [gradientColor2, setGradientColor2] = useState("#615fff");
  const [gradientDirection, setGradientDirection] = useState("to right");
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [backgroundImagePreview, setBackgroundImagePreview] = useState(null);

  const [loading, setLoading] = useState(true);
  const [roomType, setRoomType] = useState("Meeting");
  const [rooms, setRooms] = useState([]);
  const [selectedRoomId, setSelectedRoomId] = useState("");

  // YouTube streaming state
  const [youtubeEnabled, setYoutubeEnabled] = useState(false);
  const [youtubeUrl, setYoutubeUrl] = useState("rtmp://a.rtmp.youtube.com/live2");
  const [youtubeKey, setYoutubeKey] = useState("");
  const [youtubeBroadcastId, setYoutubeBroadcastId] = useState("");

  // Helper function to validate URL
  const isValidUrl = (urlString) => {
    try {
      new URL(urlString);
      return true;
    } catch (e) {
      return false;
    }
  };

  // Helper function to convert UTC date to local timezone for datetime-local input
  const convertToLocalDatetime = (utcDateString) => {
    if (!utcDateString) return "";
    const date = new Date(utcDateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Helper function to format datetime to mm/dd/yyyy hh:mm AM/PM for display
  const formatDateTimeForDisplay = (datetimeString, timezone = null) => {
    if (!datetimeString) return "";
    const date = new Date(datetimeString);
    let timezoneIdentifier = null;
    if (timezone) {
      if (typeof timezone === "string") {
        timezoneIdentifier = timezone.split("(")[0].trim();
      } else {
        timezoneIdentifier = timezone;
      }
    }
    const formatter = new Intl.DateTimeFormat("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      ...(timezoneIdentifier && { timeZone: timezoneIdentifier }),
    });
    const parts = formatter.formatToParts(date);
    const month = parts.find((p) => p.type === "month").value;
    const day = parts.find((p) => p.type === "day").value;
    const year = parts.find((p) => p.type === "year").value;
    const hour = parts.find((p) => p.type === "hour").value;
    const minute = parts.find((p) => p.type === "minute").value;
    const dayPeriod = parts.find((p) => p.type === "dayPeriod")?.value || "";
    return `${month}/${day}/${year} ${hour}:${minute} ${dayPeriod.toUpperCase()}`;
  };

  // Helper function to convert mm/dd/yyyy hh:mm AM/PM back to ISO format for storage
  const convertDisplayToISO = (displayString) => {
    if (!displayString) return "";
    try {
      const parts = displayString.match(
        /(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})\s+(AM|PM)/i
      );
      if (!parts) return "";
      const [, month, day, year, hourStr, minute, ampm] = parts;
      let hour = parseInt(hourStr);
      if (ampm.toUpperCase() === "PM" && hour !== 12) hour += 12;
      if (ampm.toUpperCase() === "AM" && hour === 12) hour = 0;
      const formattedMonth = String(month).padStart(2, "0");
      const formattedDay = String(day).padStart(2, "0");
      const formattedHour = String(hour).padStart(2, "0");
      const formattedMinute = String(minute).padStart(2, "0");
      return `${year}-${formattedMonth}-${formattedDay}T${formattedHour}:${formattedMinute}`;
    } catch (e) {
      console.error("Error converting display to ISO:", e);
      return "";
    }
  };

  // Helper function to parse date string from format "MM/DD/YYYY HH:MM AM/PM" to Date object
  const parseDateTimeString = (dateTimeString) => {
    if (!dateTimeString || dateTimeString.trim() === "") return null;
    try {
      const pattern =
        /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})\s+(AM|PM)$/i;
      const match = dateTimeString.trim().match(pattern);
      if (!match) return null;
      const [, month, day, year, hour, minute, ampm] = match;
      let hours24 = parseInt(hour, 10);
      const minutes = parseInt(minute, 10);
      if (ampm.toUpperCase() === "PM" && hours24 !== 12) {
        hours24 += 12;
      } else if (ampm.toUpperCase() === "AM" && hours24 === 12) {
        hours24 = 0;
      }
      const date = new Date(
        parseInt(year, 10),
        parseInt(month, 10) - 1,
        parseInt(day, 10),
        hours24,
        minutes
      );
      if (isNaN(date.getTime())) return null;
      return date;
    } catch (error) {
      return null;
    }
  };

  // Helper function to format date as MM/DD/YYYY
  const formatDateForInput = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const year = d.getFullYear();
    return `${month}/${day}/${year}`;
  };

  // Format date for display (MM/DD/YYYY)
  const formatDisplayDate = (dateValue) => {
    const dateToFormat =
      startDateObj ||
      (dateValue instanceof Date ? dateValue : parseDateFromInput(dateValue));
    if (!dateToFormat || isNaN(dateToFormat.getTime())) return "";
    return dateToFormat.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
  };

  // Helper function to parse date from MM/DD/YYYY
  const parseDateFromInput = (dateString) => {
    if (!dateString) return null;
    const parts = dateString.split("/");
    if (parts.length !== 3) return null;
    const month = parseInt(parts[0], 10) - 1;
    const day = parseInt(parts[1], 10);
    const year = parseInt(parts[2], 10);
    return new Date(year, month, day);
  };

  // Helper: get GMT offset string from selectedTimezone IANA name (e.g. "America/New_York" -> "-05:00")
  const getTimezoneOffsetString = () => {
    if (!selectedTimezone) return null;
    // Legacy: if timezone still has "(GMT...)" suffix, extract from that
    const legacyMatch = selectedTimezone.match(/GMT([+-]\d{2}:\d{2})/);
    if (legacyMatch) return legacyMatch[1];
    // Compute offset from IANA timezone name
    try {
      const now = new Date();
      const utcStr = now.toLocaleString('en-US', { timeZone: 'UTC' });
      const tzStr = now.toLocaleString('en-US', { timeZone: selectedTimezone });
      const diffMs = new Date(tzStr) - new Date(utcStr);
      const diffMins = Math.round(diffMs / 60000);
      const sign = diffMins >= 0 ? '+' : '-';
      const absHours = String(Math.floor(Math.abs(diffMins) / 60)).padStart(2, '0');
      const absMins = String(Math.abs(diffMins) % 60).padStart(2, '0');
      return `${sign}${absHours}:${absMins}`;
    } catch {
      return null;
    }
  };

  // Fetch rooms for the dropdown
  useEffect(() => {
    getMeetingRooms(1, 100, true)
      .then((response) => {
        setRooms(response.data || []);
      })
      .catch((err) => console.error("Error fetching rooms:", err));
  }, []);

  useEffect(() => {
    if (roomId) {
      getScheduledMeetingById(roomId)
        .then((response) => {
          const roomData = response.data;
          console.log("Room data:", roomData);

          setMeetingName(roomData.roomName || "");
          setRoomType(roomData.roomType || "Meeting");

          // Set associated room from roomPermalink
          if (roomData.roomPermalink) {
            // Find the room that matches the permalink
            getMeetingRooms(1, 100, true).then((roomsRes) => {
              const allRooms = roomsRes.data || [];
              const matchedRoom = allRooms.find(
                (r) => r.roomPermalink === roomData.roomPermalink || r.roomId === roomData.roomPermalink
              );
              if (matchedRoom) {
                setSelectedRoomId(matchedRoom.id || matchedRoom._id || "");
              }
            }).catch(() => {});
          }

          // Initialize timezone if available
          if (roomData.timezone) {
            let timezoneToMatch = roomData.timezone;
            if (
              typeof roomData.timezone === "string" &&
              roomData.timezone.includes("(")
            ) {
              timezoneToMatch = roomData.timezone.split("(")[0].trim();
            }

            const matchingTz = TIMEZONES.find(
              (tz) =>
                tz.name === timezoneToMatch ||
                tz.zone === timezoneToMatch ||
                tz.value === roomData.timezone
            );
            if (matchingTz) {
              setSelectedTimezone(matchingTz.value);
            } else {
              const systemTz =
                Intl.DateTimeFormat().resolvedOptions().timeZone;
              const fallbackTz = TIMEZONES.find(
                (tz) => tz.name === systemTz || tz.zone === systemTz
              );
              if (fallbackTz) {
                setSelectedTimezone(fallbackTz.value);
              }
            }
          }

          // Extract timezone identifier for date formatting
          let timezoneIdentifier = null;
          if (roomData.timezone) {
            if (typeof roomData.timezone === "string") {
              timezoneIdentifier = roomData.timezone.split("(")[0].trim();
            } else {
              timezoneIdentifier = roomData.timezone;
            }
          }

          // Format dates for display
          const startTime = roomData.scheduledStartTime
            ? formatDateTimeForDisplay(
                new Date(roomData.scheduledStartTime),
                timezoneIdentifier
              )
            : "";
          const endTime = roomData.scheduledEndTime
            ? formatDateTimeForDisplay(
                new Date(roomData.scheduledEndTime),
                timezoneIdentifier
              )
            : "";

          setMeetingStartTime(startTime);
          setMeetingEndTime(endTime);

          // Initialize date/time picker fields using the room's timezone
          if (roomData.scheduledStartTime) {
            const baseDate = new Date(roomData.scheduledStartTime);

            let hours, minutes, dateForInput;
            if (timezoneIdentifier) {
              const formatter = new Intl.DateTimeFormat("en-US", {
                timeZone: timezoneIdentifier,
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
              });
              const parts = formatter.formatToParts(baseDate);
              const month = parts.find((p) => p.type === "month").value;
              const day = parts.find((p) => p.type === "day").value;
              const year = parts.find((p) => p.type === "year").value;
              const hourStr = parts.find((p) => p.type === "hour").value;
              const minuteStr = parts.find((p) => p.type === "minute").value;
              const dayPeriod =
                parts.find((p) => p.type === "dayPeriod")?.value || "";

              hours = parseInt(hourStr, 10);
              minutes = parseInt(minuteStr, 10);

              // Create a date object for the date picker using extracted components
              // We store just the date part; hours/minutes are tracked separately
              dateForInput = new Date(
                parseInt(year, 10),
                parseInt(month, 10) - 1,
                parseInt(day, 10),
                0,
                0,
                0,
                0
              );

              setStartAMPM(dayPeriod.toUpperCase());
            } else {
              dateForInput = baseDate;
              hours = baseDate.getHours();
              minutes = baseDate.getMinutes();
              const ampm = hours >= 12 ? "PM" : "AM";
              setStartAMPM(ampm);
              // Convert from 24-hour to 12-hour format
              hours = hours % 12 || 12;
            }

            setStartDateObj(dateForInput);
            setStartDate(formatDateForInput(dateForInput));
            // hours from Intl formatter is already in 12-hour format (1-12)
            if (!timezoneIdentifier) {
              hours = hours % 12 || 12;
            }
            setStartHour(String(hours));
            setStartMinute(String(minutes).padStart(2, "0"));

            // Initialize duration from endTime if available
            if (roomData.scheduledEndTime) {
              const endDate = new Date(roomData.scheduledEndTime);
              const diffMs = endDate.getTime() - baseDate.getTime();
              const diffMinutes = Math.round(diffMs / (1000 * 60));
              if (diffMinutes > 0) {
                setDuration(diffMinutes.toString());
              }
            }
            setIsInitialized(true);
          }

          setInvitesList(roomData.emails || []);

          // Hydrate structured invitees list. Prefer roomData.invitees (with names);
          // fall back to legacy emails array (no names).
          if (Array.isArray(roomData.invitees) && roomData.invitees.length > 0) {
            setInviteesList(
              roomData.invitees.map((inv) => ({
                email: inv.email || "",
                name: inv.name || "",
              }))
            );
          } else if (Array.isArray(roomData.emails) && roomData.emails.length > 0) {
            setInviteesList(roomData.emails.map((e) => ({ email: e, name: "" })));
          } else {
            setInviteesList([]);
          }

          setRoomSettings({
            allowChat: roomData.allowChat ?? true,
            allowScreenSharing: roomData.allowScreenSharing ?? false,
            muteParticipantsOnEntry:
              roomData.muteParticipantsOnEntry ?? false,
            allowReactions: roomData.allowReactions ?? true,
            allowRaiseHand: roomData.allowRaiseHand ?? true,
            requirePassword: roomData.requirePassword ?? false,
            password: roomData.password || "",
            hostApprovalRequired: roomData.hostApprovalRequired ?? false,
          });

          const hasExitUrl =
            roomData.exitUrl && roomData.exitUrl.trim() !== "";
          setExitUrlEnabled(roomData.exitUrlEnabled ?? hasExitUrl);
          setExitUrl(roomData.exitUrl || "");

          const hasCustomButton =
            roomData.customButtonText &&
            roomData.customButtonText.trim() !== "" &&
            roomData.customButtonUrl &&
            roomData.customButtonUrl.trim() !== "";
          setCustomButtonEnabled(
            roomData.customButtonEnabled ?? hasCustomButton
          );
          setCustomButtonText(roomData.customButtonText || "");
          setCustomButtonUrl(roomData.customButtonUrl || "");

          const hasWaitingRoomText =
            (roomData.waitingRoomTitle &&
              roomData.waitingRoomTitle.trim() !== "") ||
            (roomData.waitingRoomMessage &&
              roomData.waitingRoomMessage.trim() !== "") ||
            (roomData.waitingRoomSubText &&
              roomData.waitingRoomSubText.trim() !== "");
          setWaitingRoomTextEnabled(
            roomData.waitingRoomTextEnabled ?? hasWaitingRoomText
          );
          setWaitingRoomTitle(roomData.waitingRoomTitle || "");
          setWaitingRoomMessage(roomData.waitingRoomMessage || "");
          setWaitingRoomSubText(roomData.waitingRoomSubText || "");

          const backgroundImageUrl =
            roomData.meetingBackgroundImage || roomData.backgroundImageUrl;
          const hasBackgroundImage =
            backgroundImageUrl && backgroundImageUrl.trim() !== "";
          const hasCustomColor =
            roomData.backgroundColor &&
            roomData.backgroundColor !== "" &&
            roomData.backgroundColor !== "#1a1a2e";
          const hasCustomGradient =
            roomData.meetingBackgroundGradient ||
            (roomData.gradientColor1 &&
              roomData.gradientColor1 !== "" &&
              roomData.gradientColor1 !== "#1a1a2e") ||
            (roomData.gradientColor2 &&
              roomData.gradientColor2 !== "" &&
              roomData.gradientColor2 !== "#615fff");

          const hasCustomBackground =
            hasBackgroundImage || hasCustomColor || hasCustomGradient;

          setCustomBackgroundEnabled(
            roomData.customBackgroundEnabled ?? hasCustomBackground
          );

          let detectedBackgroundType = "solid";
          if (hasBackgroundImage) {
            detectedBackgroundType = "image";
          } else if (hasCustomGradient) {
            detectedBackgroundType = "gradient";
          }

          setBackgroundType(
            roomData.backgroundType || detectedBackgroundType
          );
          setBackgroundColor(roomData.backgroundColor || "#1a1a2e");
          setGradientColor1(roomData.gradientColor1 || "#1a1a2e");
          setGradientColor2(roomData.gradientColor2 || "#615fff");
          setGradientDirection(roomData.gradientDirection || "to right");

          if (hasBackgroundImage) {
            setBackgroundImagePreview(backgroundImageUrl);
          }

          // YouTube streaming fields
          if (roomData.streamYouTubeUrl || roomData.streamYouTubeKey) {
            setYoutubeEnabled(true);
            setYoutubeUrl(roomData.streamYouTubeUrl || "rtmp://a.rtmp.youtube.com/live2");
            setYoutubeKey(roomData.streamYouTubeKey || "");
            setYoutubeBroadcastId(roomData.streamYouTubeBroadcastId || "");
          }

          setLoading(false);
        })
        .catch((error) => {
          console.error("Error fetching room data:", error);
          toast.error("Failed to load meeting data");
          setLoading(false);
        });
    }
  }, [roomId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Calculate end time and update meetingStartTime/meetingEndTime
  useEffect(() => {
    if (
      isInitialized &&
      startDateObj &&
      startHour &&
      startMinute !== "" &&
      startAMPM
    ) {
      const dateObj = new Date(startDateObj);
      if (dateObj && startHour && startMinute) {
        let hours24 = parseInt(startHour, 10);
        if (startAMPM === "PM" && hours24 !== 12) {
          hours24 += 12;
        } else if (startAMPM === "AM" && hours24 === 12) {
          hours24 = 0;
        }
        const minutes = parseInt(startMinute, 10) || 0;
        dateObj.setHours(hours24, minutes, 0, 0);

        let timezoneIdentifier = null;
        if (selectedTimezone) {
          if (typeof selectedTimezone === "string") {
            timezoneIdentifier = selectedTimezone.split("(")[0].trim();
          } else {
            timezoneIdentifier = selectedTimezone;
          }
        }

        const formattedStart = formatDateTimeForDisplay(
          dateObj.toISOString(),
          timezoneIdentifier
        );
        if (formattedStart !== meetingStartTime) {
          setMeetingStartTime(formattedStart);
        }

        const durationMins = parseInt(duration || "60", 10);
        if (durationMins > 0) {
          const endDate = new Date(dateObj);
          endDate.setMinutes(endDate.getMinutes() + durationMins);

          const formattedEnd = formatDateTimeForDisplay(
            endDate.toISOString(),
            timezoneIdentifier
          );
          if (formattedEnd !== meetingEndTime) {
            setMeetingEndTime(formattedEnd);
          }
        }
      }
    }
  }, [
    startDateObj,
    startHour,
    startMinute,
    startAMPM,
    duration,
    isInitialized,
    selectedTimezone,
    meetingStartTime,
    meetingEndTime,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSettingChange = (setting, value) => {
    setRoomSettings((prev) => ({
      ...prev,
      [setting]: value,
    }));
  };

  // Generate suggestions based on input (Co-Host)
  useEffect(() => {
    const input = invitesEmailInput.trim().toLowerCase();

    if (input.length === 0) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const newSuggestions = [];
    const hasAtSymbol = input.includes("@");
    const parts = input.split("@");

    if (hasAtSymbol && parts.length === 2) {
      const [localPart, domainPart] = parts;

      if (domainPart.length > 0) {
        commonDomains.forEach((domain) => {
          if (domain.startsWith(domainPart) && domain !== domainPart) {
            const suggestion = `${localPart}@${domain}`;
            if (!invitesList.includes(suggestion)) {
              newSuggestions.push(suggestion);
            }
          }
        });
      } else {
        commonDomains.forEach((domain) => {
          const suggestion = `${localPart}@${domain}`;
          if (!invitesList.includes(suggestion)) {
            newSuggestions.push(suggestion);
          }
        });
      }
    } else if (!hasAtSymbol) {
      commonDomains.forEach((domain) => {
        const suggestion = `${input}@${domain}`;
        if (!invitesList.includes(suggestion)) {
          newSuggestions.push(suggestion);
        }
      });
    }

    setSuggestions(newSuggestions.slice(0, 5));

    if (newSuggestions.length > 0) {
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }

    setSelectedSuggestionIndex(-1);
  }, [invitesEmailInput, invitesList]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check if user exists when valid email is entered (Co-Host)
  useEffect(() => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const input = invitesEmailInput.trim();

    if (input.length === 0) {
      setUserExists(null);
      setCheckingUser(false);
      return;
    }

    if (emailRegex.test(input)) {
      const timeoutId = setTimeout(async () => {
        setCheckingUser(true);
        try {
          const response = await userService.checkUserExists(input);
          setUserExists(response.exists);
        } catch (error) {
          setUserExists(false);
        } finally {
          setCheckingUser(false);
        }
      }, 500);

      return () => clearTimeout(timeoutId);
    } else {
      setUserExists(null);
      setCheckingUser(false);
    }
  }, [invitesEmailInput]);

  // (Speaker autosuggest/check effects removed — Speakers section replaced by Invitees)

  const handleAddCoHost = (email, isInvite) => {
    const trimmedEmail = email.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(trimmedEmail)) {
      toast.error("Please enter a valid email address");
      return;
    }

    if (!invitesList.includes(trimmedEmail)) {
      setInvitesList((prev) => [...prev, trimmedEmail]);
      if (isInvite) {
        setInviteEmails((prev) => [...prev, trimmedEmail]);
      }
      setInvitesEmailInput("");
      setShowSuggestions(false);
      setUserExists(null);
      toast.success(isInvite ? "Email invite added" : "Co-host added");
    } else {
      toast.warning("Email already added.");
    }
  };

  const handleAddInvitee = () => {
    const email = newInviteeEmail.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error("Please enter a valid email address");
      return;
    }
    if (inviteesList.some((inv) => inv.email.toLowerCase() === email.toLowerCase())) {
      toast.warning("This invitee is already added");
      return;
    }
    setInviteesList((prev) => [...prev, { email, name: newInviteeName.trim() }]);
    setNewInviteeEmail("");
    setNewInviteeName("");
    toast.success("Invitee added");
  };

  const handleRemoveInvitee = (email) => {
    setInviteesList((prev) => prev.filter((inv) => inv.email !== email));
  };

  const handleUpdateMeeting = async () => {
    const roomTypeLower = roomType.toLowerCase();

    // Validation
    if (!meetingName.trim()) {
      toast.error(`Please enter a ${roomTypeLower} name`);
      return;
    }

    if (meetingName.trim().length < 3) {
      toast.error(`${roomType} name must be at least 3 characters long`);
      return;
    }

    if (roomSettings.requirePassword && !roomSettings.password.trim()) {
      toast.error(`Please enter a password for the ${roomTypeLower}`);
      return;
    }

    if (exitUrlEnabled) {
      if (!exitUrl.trim()) {
        toast.error("Please enter an exit URL");
        return;
      }
      if (!isValidUrl(exitUrl.trim())) {
        toast.error("Please enter a valid URL (e.g., https://example.com)");
        return;
      }
    }

    if (customButtonEnabled) {
      if (!customButtonText.trim()) {
        toast.error("Please enter a button name");
        return;
      }
      if (customButtonText.trim().length < 3) {
        toast.error("Button name must be at least 3 characters long");
        return;
      }
      if (!customButtonUrl.trim()) {
        toast.error("Please enter a button URL");
        return;
      }
      if (!isValidUrl(customButtonUrl.trim())) {
        toast.error(
          "Please enter a valid button URL (e.g., https://example.com)"
        );
        return;
      }
    }

    if (waitingRoomTextEnabled) {
      if (!waitingRoomTitle.trim()) {
        toast.error("Please enter waiting room title");
        return;
      }
      if (waitingRoomTitle.trim().length < 3) {
        toast.error("Waiting room title must be at least 3 characters long");
        return;
      }
      if (!waitingRoomMessage.trim()) {
        toast.error("Please enter waiting room message");
        return;
      }
      if (waitingRoomMessage.trim().length < 3) {
        toast.error(
          "Waiting room message must be at least 3 characters long"
        );
        return;
      }
      if (!waitingRoomSubText.trim()) {
        toast.error("Please enter waiting room sub text");
        return;
      }
      if (waitingRoomSubText.trim().length < 3) {
        toast.error(
          "Waiting room sub text must be at least 3 characters long"
        );
        return;
      }
    }

    if (customBackgroundEnabled) {
      if (
        backgroundType === "image" &&
        !backgroundImage &&
        !backgroundImagePreview
      ) {
        toast.error("Please select a background image");
        return;
      }
      if (backgroundType === "image" && backgroundImage) {
        const maxSize = 5 * 1024 * 1024;
        if (backgroundImage.size > maxSize) {
          toast.error("Background image size should not exceed 5MB");
          return;
        }
      }
      if (backgroundType === "gradient") {
        if (!gradientColor1 || !gradientColor2) {
          toast.error("Please select both gradient colors");
          return;
        }
      }
      if (backgroundType === "solid" && !backgroundColor) {
        toast.error("Please select a background color");
        return;
      }
    }

    try {
      const formData = new FormData();

      formData.append("roomId", roomId);
      formData.append("roomName", meetingName.trim());
      formData.append("roomType", roomType);

      // Associate with selected room for Meetings
      if (roomType === "Meeting" && selectedRoomId) {
        formData.append("associatedRoomId", selectedRoomId);
      }

      // Extract timezone value from selectedTimezone
      let timezoneValue = "America/New_York";
      if (selectedTimezone) {
        if (typeof selectedTimezone === "string") {
          if (selectedTimezone.includes("(")) {
            timezoneValue = selectedTimezone.split("(")[0].trim();
          } else if (selectedTimezone.includes(" ")) {
            timezoneValue = selectedTimezone.split(" ")[0].trim();
          } else {
            timezoneValue = selectedTimezone.trim();
          }
        } else {
          timezoneValue = selectedTimezone;
        }
      }

      if (!timezoneValue || timezoneValue === "") {
        timezoneValue = "America/New_York";
      }

      formData.append("timezone", timezoneValue);

      // Validate that we have all required state values
      if (!startDateObj || !startHour || !startMinute || !startAMPM) {
        toast.error("Please select a valid date and time");
        return;
      }

      // Send the individual time components to the backend
      formData.append("startTimeHour", startHour.toString());
      formData.append("startTimeMinute", startMinute);
      formData.append("StartTimeAmPm", startAMPM.toUpperCase());

      if (duration && parseInt(duration, 10) > 0) {
        formData.append("duration", duration.toString());
      }

      // FIX: Build startTime ISO string using the date components directly
      // with the timezone offset, instead of using new Date().toISOString()
      // which incorrectly converts using browser timezone.
      const year = startDateObj.getFullYear();
      const month = startDateObj.getMonth();
      const day = startDateObj.getDate();

      // Convert 12-hour format to 24-hour format
      let hours24 = parseInt(startHour, 10);
      if (startAMPM === "PM" && hours24 !== 12) {
        hours24 += 12;
      } else if (startAMPM === "AM" && hours24 === 12) {
        hours24 = 0;
      }
      const mins = parseInt(startMinute, 10) || 0;

      // Build an ISO-like string with the timezone offset appended
      // so that the backend receives the correct absolute time
      const offsetStr = getTimezoneOffsetString();
      const pad2 = (n) => String(n).padStart(2, "0");
      const isoDatePart = `${year}-${pad2(month + 1)}-${pad2(day)}T${pad2(hours24)}:${pad2(mins)}:00`;

      if (offsetStr) {
        // Append the timezone offset so the backend can parse the exact UTC time
        formData.append("startTime", `${isoDatePart}${offsetStr}`);
      } else {
        // Fallback: create Date object (browser local) and convert to ISO
        const dateObj = new Date(year, month, day, hours24, mins, 0);
        const isoString = dateObj.toISOString();
        const isoWithoutMillis = isoString.replace(/\.\d{3}Z$/, "");
        formData.append("startTime", isoWithoutMillis);
      }

      // Add endTime
      if (duration && parseInt(duration, 10) > 0) {
        const durationMins = parseInt(duration, 10);
        // Calculate end time by adding duration to start components
        const startTotalMins = hours24 * 60 + mins + durationMins;
        const endDay = day + Math.floor(startTotalMins / (24 * 60));
        const endHours = Math.floor((startTotalMins % (24 * 60)) / 60);
        const endMins = startTotalMins % 60;
        // Use Date to handle month/year rollover properly
        const endDateObj = new Date(year, month, day, hours24, mins + durationMins, 0);
        const endYear = endDateObj.getFullYear();
        const endMonth = endDateObj.getMonth();
        const endDayOfMonth = endDateObj.getDate();
        const endH = endDateObj.getHours();
        const endM = endDateObj.getMinutes();
        const endIsoPart = `${endYear}-${pad2(endMonth + 1)}-${pad2(endDayOfMonth)}T${pad2(endH)}:${pad2(endM)}:00`;

        if (offsetStr) {
          formData.append("endTime", `${endIsoPart}${offsetStr}`);
        } else {
          const endIsoString = endDateObj.toISOString();
          const endIsoWithoutMillis = endIsoString.replace(/\.\d{3}Z$/, "");
          formData.append("endTime", endIsoWithoutMillis);
        }
      }

      Object.keys(roomSettings).forEach((key) => {
        const value = roomSettings[key];
        if (value !== null && value !== undefined && value !== "") {
          formData.append(key, value);
        }
      });

      if (invitesList.length > 0) {
        formData.append("emails", JSON.stringify(invitesList));
      }

      // Always send invitees so removals also persist (use empty array, not skipped)
      formData.append("invitees", JSON.stringify(inviteesList));

      formData.append("exitUrlEnabled", exitUrlEnabled);
      if (exitUrlEnabled && exitUrl.trim()) {
        formData.append("exitUrl", exitUrl.trim());
      }

      formData.append("customButtonEnabled", customButtonEnabled);
      if (customButtonEnabled) {
        formData.append("customButtonText", customButtonText.trim());
        formData.append("customButtonUrl", customButtonUrl.trim());
      }

      formData.append("waitingRoomTextEnabled", waitingRoomTextEnabled);
      if (waitingRoomTextEnabled) {
        formData.append("waitingRoomTitle", waitingRoomTitle.trim());
        formData.append("waitingRoomMessage", waitingRoomMessage.trim());
        formData.append("waitingRoomSubText", waitingRoomSubText.trim());
      }

      formData.append("customBackgroundEnabled", customBackgroundEnabled);

      if (customBackgroundEnabled) {
        formData.append("backgroundType", backgroundType);

        if (backgroundType === "solid") {
          formData.append("backgroundColor", backgroundColor);
        } else if (backgroundType === "gradient") {
          formData.append("gradientColor1", gradientColor1);
          formData.append("gradientColor2", gradientColor2);
          formData.append("gradientDirection", gradientDirection);
        } else if (backgroundType === "image" && backgroundImage) {
          formData.append("backgroundImage", backgroundImage);
        }
      }

      // YouTube streaming fields
      if (roomType === "Live Stream" && youtubeEnabled) {
        formData.append("streamYouTubeUrl", youtubeUrl);
        if (youtubeKey) {
          formData.append("streamYouTubeKey", youtubeKey);
        }
        if (youtubeBroadcastId) {
          formData.append("streamYouTubeBroadcastId", youtubeBroadcastId);
        }
      }

      console.log("FormData contents:");
      for (let [key, value] of formData.entries()) {
        if (value instanceof File) {
          console.log(
            `${key}:`,
            `File - ${value.name} (${value.size} bytes)`
          );
        } else {
          console.log(`${key}:`, value);
        }
      }

      const response = await updateScheduledMeeting(roomId, formData);

      console.log(`${roomType} updated:`, response);
      toast.success(`${roomType} updated successfully!`);

      navigate("/dashboard");
    } catch (error) {
      console.error(`Error updating ${roomTypeLower}:`, error);
      const errorMessage =
        error.response?.data?.message ||
        `Failed to update ${roomTypeLower}. Please try again.`;
      toast.error(errorMessage);
    }
  };

  // Reusable styling constants
  const inputClass =
    "w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#6a1fbf] focus:ring-1 focus:ring-[#6a1fbf] transition-colors";
  const selectClass =
    "px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#6a1fbf] focus:ring-1 focus:ring-[#6a1fbf] transition-colors appearance-none cursor-pointer";
  const selectArrowStyle = {
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23fff' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 1rem center",
  };

  // Toggle switch component
  const ToggleSwitch = ({ enabled, onChange }) => (
    <button
      type="button"
      onClick={() => onChange(!enabled)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
        enabled ? "bg-[#6a1fbf]" : "bg-white/10"
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          enabled ? "translate-x-6" : "translate-x-1"
        }`}
      />
    </button>
  );

  if (loading) {
    return (
      <div className="min-h-screen text-white p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#6A1FBF] mx-auto mb-4"></div>
          <p className="text-gray-400">
            Loading {roomType === "Webinar" ? "webinar" : "meeting"} data...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 p-3 md:p-6">
      {/* Header */}
      <div>
        <button
          onClick={() => navigate("/dashboard")}
          className="flex items-center text-gray-400 hover:text-white mb-4 transition-colors"
        >
          <ArrowLeft size={20} className="mr-2" />
          Back to Dashboard
        </button>
        <h2 className="text-2xl font-bold text-white mb-2">
          Edit{" "}
          {roomType === "Webinar"
            ? "Live Webinar"
            : roomType === "Live Stream"
            ? "Live Stream"
            : "Scheduled Meeting"}
        </h2>
        <p className="text-gray-400">
          Update your {roomType.toLowerCase()} details and settings
        </p>
      </div>

      {/* Basic Details */}
      <div className="bg-white/5 rounded-2xl border border-white/10 p-6 space-y-6">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <CalendarDays className="w-5 h-5 text-[#6a1fbf]" />
          Basic Details
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              {roomType} Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={meetingName}
              onChange={(e) => setMeetingName(e.target.value.replace(/\s+/g, "-"))}
              placeholder={`Enter-${roomType.toLowerCase()}-name`}
              maxLength={25}
              className={`${inputClass} ${
                meetingName.trim() && meetingName.trim().length < 3
                  ? "!border-red-500 !focus:ring-red-500"
                  : ""
              }`}
            />
            <p className="text-gray-500 text-xs mt-1">
              {meetingName.trim() && meetingName.trim().length < 3 ? (
                <span className="text-red-400">
                  Minimum 3 characters required
                </span>
              ) : (
                "25 characters max"
              )}
            </p>
          </div>

          {/* Room Selection (Meetings only) */}
          {roomType === "Meeting" && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Room
              </label>
              <select
                value={selectedRoomId}
                onChange={(e) => setSelectedRoomId(e.target.value)}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#6a1fbf] focus:ring-1 focus:ring-[#6a1fbf] transition-colors appearance-none cursor-pointer"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23fff' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "right 1rem center",
                }}
              >
                <option value="" style={{ backgroundColor: '#1a1a2e', color: '#fff' }}>Select a room</option>
                {rooms.map((room) => (
                  <option key={room.id || room._id} value={room.id || room._id} style={{ backgroundColor: '#1a1a2e', color: '#fff' }}>
                    {room.roomName || room.name || "Unnamed Room"}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Date & Time */}
      <div className="bg-white/5 rounded-2xl border border-white/10 p-6 space-y-6">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <Clock className="w-5 h-5 text-[#6a1fbf]" />
          Date & Time
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Date picker */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Start Date
            </label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                readOnly
                value={
                  startDateObj
                    ? formatDisplayDate(startDateObj)
                    : startDate || "MM/DD/YYYY"
                }
                className={`${inputClass} cursor-pointer`}
                onClick={() => setShowStartCalendar((prev) => !prev)}
              />
              <button
                type="button"
                onClick={() => setShowStartCalendar((prev) => !prev)}
                className="p-3 bg-white/5 border border-white/10 rounded-lg hover:bg-white/10 transition-colors"
              >
                <CalendarDays className="h-5 w-5 text-[#6a1fbf]" />
              </button>
            </div>
            {showStartCalendar && (
              <div className="mt-3">
                <div className="w-full max-w-[320px] rounded-2xl border border-white/10 bg-[#1C1B29] p-4">
                  <Calendar
                    value={
                      startDateObj ||
                      parseDateFromInput(startDate) ||
                      new Date()
                    }
                    minDate={new Date()}
                    onChange={(val) => {
                      const date =
                        val instanceof Date ? val : new Date(val);
                      date.setHours(0, 0, 0, 0);
                      setStartDateObj(date);
                      setStartDate(formatDateForInput(date));
                      setShowStartCalendar(false);
                    }}
                    calendarType="gregory"
                    className="text-white"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Time picker */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Start Time
            </label>
            <div className="flex items-center gap-2">
              <select
                value={`${startHour}-${startMinute}`}
                onChange={(e) => {
                  const [hourStr, minuteStr] = e.target.value.split("-");
                  setStartHour(hourStr);
                  setStartMinute(minuteStr);
                }}
                className={`${selectClass} flex-1`}
                style={selectArrowStyle}
              >
                {TIME_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <select
                value={startAMPM}
                onChange={(e) => setStartAMPM(e.target.value)}
                className={`${selectClass} w-24`}
                style={selectArrowStyle}
              >
                <option value="AM">AM</option>
                <option value="PM">PM</option>
              </select>
            </div>
          </div>

          {/* Duration */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Duration
            </label>
            <div className="flex items-center gap-2">
              <select
                value={durationHours}
                onChange={(e) => {
                  const hours = parseInt(e.target.value, 10);
                  const totalMinutes = hours * 60 + durationMinutes;
                  setDuration(totalMinutes.toString());
                }}
                className={`${selectClass} w-24`}
                style={selectArrowStyle}
              >
                {[...Array(13).keys()].map((hour) => (
                  <option key={hour} value={hour}>
                    {hour}
                  </option>
                ))}
              </select>
              <span className="text-sm text-gray-400">hr</span>
              <select
                value={durationMinutes}
                onChange={(e) => {
                  const minutes = parseInt(e.target.value, 10);
                  const totalMinutes = durationHours * 60 + minutes;
                  setDuration(totalMinutes.toString());
                }}
                className={`${selectClass} w-24`}
                style={selectArrowStyle}
              >
                {[0, 15, 30, 45].map((minute) => (
                  <option key={minute} value={minute}>
                    {minute}
                  </option>
                ))}
              </select>
              <span className="text-sm text-gray-400">min</span>
            </div>
          </div>

          {/* Timezone */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Timezone
            </label>
            <select
              value={selectedTimezone}
              onChange={(e) => setSelectedTimezone(e.target.value)}
              className={`${selectClass} w-full`}
              style={selectArrowStyle}
            >
              {timezones.map((tz) => (
                <option key={tz.value} value={tz.value}>
                  {tz.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Participants */}
      <div className="bg-white/5 rounded-2xl border border-white/10 p-6 space-y-6">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <Users className="w-5 h-5 text-[#6a1fbf]" />
          Participants
        </h3>

        {/* Co-Host */}
        {user?.plan == "Business" && (
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Co-Host
            </label>
            <div className="flex items-center gap-2 mb-3">
              <div className="flex-1 relative">
                <input
                  type="email"
                  value={invitesEmailInput}
                  onChange={(e) => setInvitesEmailInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      if (
                        selectedSuggestionIndex >= 0 &&
                        suggestions[selectedSuggestionIndex]
                      ) {
                        e.preventDefault();
                        const selectedEmail =
                          suggestions[selectedSuggestionIndex];
                        handleAddCoHost(selectedEmail, null);
                        setSelectedSuggestionIndex(-1);
                      } else if (
                        invitesEmailInput.trim() &&
                        !checkingUser
                      ) {
                        e.preventDefault();
                        const email = invitesEmailInput.trim();
                        handleAddCoHost(email, userExists === false);
                      }
                    } else if (
                      e.key === "ArrowDown" &&
                      suggestions.length > 0
                    ) {
                      e.preventDefault();
                      setShowSuggestions(true);
                      setSelectedSuggestionIndex((prev) =>
                        prev < suggestions.length - 1 ? prev + 1 : prev
                      );
                    } else if (
                      e.key === "ArrowUp" &&
                      suggestions.length > 0
                    ) {
                      e.preventDefault();
                      setShowSuggestions(true);
                      setSelectedSuggestionIndex((prev) =>
                        prev > 0 ? prev - 1 : 0
                      );
                    } else if (e.key === "Escape") {
                      setShowSuggestions(false);
                      setSelectedSuggestionIndex(-1);
                    }
                  }}
                  onFocus={() => {
                    if (invitesEmailInput.trim().length > 0) {
                      setShowSuggestions(true);
                    }
                  }}
                  onBlur={() => {
                    setTimeout(() => setShowSuggestions(false), 200);
                  }}
                  placeholder="Add co-host email and press Enter"
                  className={inputClass}
                />
                {invitesEmailInput.trim() && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    {checkingUser ? (
                      <div className="w-5 h-5 border-2 border-[#6a1fbf] border-t-transparent rounded-full animate-spin"></div>
                    ) : userExists === true ? (
                      <div className="text-green-500 text-sm">✓</div>
                    ) : userExists === false ? (
                      <Mail className="w-5 h-5 text-orange-500" />
                    ) : null}
                  </div>
                )}
                {showSuggestions && suggestions.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-[#1C1B29] border border-white/10 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {suggestions.map((suggestion, index) => (
                      <div
                        key={index}
                        onClick={() => handleAddCoHost(suggestion, null)}
                        className={`px-4 py-2 cursor-pointer hover:bg-white/10 text-sm text-gray-300 ${
                          selectedSuggestionIndex === index
                            ? "bg-white/10"
                            : ""
                        }`}
                      >
                        {suggestion}
                      </div>
                    ))}
                  </div>
                )}
                {!showSuggestions &&
                  invitesEmailInput.trim() &&
                  userExists !== null &&
                  !checkingUser && (
                    <div className="absolute z-50 w-full mt-1 bg-[#1C1B29] border border-white/10 rounded-lg shadow-lg">
                      {userExists ? (
                        <div
                          onClick={() =>
                            handleAddCoHost(invitesEmailInput.trim(), false)
                          }
                          className="px-4 py-3 cursor-pointer hover:bg-white/10"
                        >
                          <div className="flex items-center gap-2">
                            <div className="text-green-500">✓</div>
                            <div>
                              <div className="text-sm font-medium text-gray-300">
                                {invitesEmailInput.trim()}
                              </div>
                              <div className="text-xs text-gray-500">
                                User exists - Add as Co-Host
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div
                          onClick={() =>
                            handleAddCoHost(invitesEmailInput.trim(), true)
                          }
                          className="px-4 py-3 cursor-pointer hover:bg-white/10"
                        >
                          <div className="flex items-center gap-2">
                            <Mail className="w-5 h-5 text-orange-500" />
                            <div>
                              <div className="text-sm font-medium text-gray-300">
                                {invitesEmailInput.trim()}
                              </div>
                              <div className="text-xs text-gray-500">
                                User not found - Send Email Invite
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
              </div>
              <button
                onClick={() => {
                  if (invitesEmailInput.trim()) {
                    const email = invitesEmailInput.trim();
                    if (userExists === false) {
                      handleAddCoHost(email, true);
                    } else {
                      handleAddCoHost(email, false);
                    }
                  }
                }}
                disabled={checkingUser}
                className="px-4 py-3 bg-[#6a1fbf] text-white rounded-lg hover:bg-[#7B2FCF] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {checkingUser ? "..." : "Add"}
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {invitesList.map((email, index) => {
                const isInvite = inviteEmails.includes(email);
                return (
                  <span
                    key={index}
                    className={`flex items-center px-3 py-1.5 rounded-full text-sm ${
                      isInvite
                        ? "bg-orange-500/20 border border-orange-500/30 text-orange-400"
                        : "bg-white/5 border border-white/10 text-gray-300"
                    }`}
                  >
                    {isInvite && <Mail className="w-3 h-3 mr-1" />}
                    {email}
                    <button
                      onClick={() => {
                        setInvitesList((prev) =>
                          prev.filter((item) => item !== email)
                        );
                        setInviteEmails((prev) =>
                          prev.filter((item) => item !== email)
                        );
                      }}
                      className="ml-2 text-gray-500 hover:text-white"
                    >
                      <X size={14} />
                    </button>
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {/* Invitees */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Invitees
          </label>
          <p className="text-xs text-gray-500 mb-3">
            They will receive a personalized email invite when you save changes.
          </p>
          <div className="flex items-center gap-2 mb-3">
            <input
              type="email"
              value={newInviteeEmail}
              onChange={(e) => setNewInviteeEmail(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAddInvitee();
                }
              }}
              placeholder="Email"
              className={`${inputClass} flex-1`}
            />
            <input
              type="text"
              value={newInviteeName}
              onChange={(e) => setNewInviteeName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAddInvitee();
                }
              }}
              placeholder="Name (optional)"
              className={`${inputClass} flex-1`}
            />
            <button
              onClick={handleAddInvitee}
              className="px-4 py-3 bg-[#6a1fbf] text-white rounded-lg hover:bg-[#7B2FCF] transition-colors"
            >
              Add
            </button>
          </div>
          {inviteesList.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {inviteesList.map((inv, index) => (
                <span
                  key={`${inv.email}-${index}`}
                  className="flex items-center px-3 py-1.5 rounded-full text-sm bg-white/5 border border-white/10 text-gray-300"
                >
                  <Mail className="w-3 h-3 mr-1.5 text-gray-500" />
                  {inv.name ? `${inv.name} <${inv.email}>` : inv.email}
                  <button
                    onClick={() => handleRemoveInvitee(inv.email)}
                    className="ml-2 text-gray-500 hover:text-white"
                  >
                    <X size={14} />
                  </button>
                </span>
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-500">No invitees yet.</p>
          )}
        </div>
      </div>

      {/* Room Settings */}
      <div className="bg-white/5 rounded-2xl border border-white/10 p-6 space-y-6">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <Settings className="w-5 h-5 text-[#6a1fbf]" />
          Room Settings
        </h3>

        <div className="space-y-5">
          {/* Allow Chat */}
          <div className="flex items-center justify-between py-2">
            <div>
              <h4 className="text-white font-medium text-sm">Allow Chat</h4>
              <p className="text-gray-500 text-xs mt-0.5">
                Enable or disable in-meeting chat for participants
              </p>
            </div>
            <ToggleSwitch
              enabled={roomSettings.allowChat}
              onChange={(val) => handleSettingChange("allowChat", val)}
            />
          </div>

          {/* Allow Screen Sharing */}
          {roomType !== "Webinar" && (
            <div className="flex items-center justify-between py-2 border-t border-white/5">
              <div>
                <h4 className="text-white font-medium text-sm">
                  Allow Screen Sharing
                </h4>
                <p className="text-gray-500 text-xs mt-0.5">
                  Let participants share their screen during the meeting
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() =>
                    handleSettingChange("allowScreenSharing", true)
                  }
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    roomSettings.allowScreenSharing === true
                      ? "bg-[#6a1fbf] text-white"
                      : "bg-white/5 text-gray-400 hover:bg-white/10"
                  }`}
                >
                  Everyone
                </button>
                <button
                  onClick={() =>
                    handleSettingChange("allowScreenSharing", false)
                  }
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    roomSettings.allowScreenSharing === false
                      ? "bg-[#6a1fbf] text-white"
                      : "bg-white/5 text-gray-400 hover:bg-white/10"
                  }`}
                >
                  Host only
                </button>
              </div>
            </div>
          )}

          {/* Mute participants on entry */}
          {roomType !== "Webinar" && (
            <div className="flex items-center justify-between py-2 border-t border-white/5">
              <div>
                <h4 className="text-white font-medium text-sm">
                  Mute Participants on Entry
                </h4>
                <p className="text-gray-500 text-xs mt-0.5">
                  Hosts can unmute participants later
                </p>
              </div>
              <ToggleSwitch
                enabled={roomSettings.muteParticipantsOnEntry}
                onChange={(val) =>
                  handleSettingChange("muteParticipantsOnEntry", val)
                }
              />
            </div>
          )}

          {/* Allow Reactions */}
          <div className="flex items-center justify-between py-2 border-t border-white/5">
            <div>
              <h4 className="text-white font-medium text-sm">
                Allow Reactions
              </h4>
              <p className="text-gray-500 text-xs mt-0.5">
                Participants can use emoji reactions during the meeting
              </p>
            </div>
            <ToggleSwitch
              enabled={roomSettings.allowReactions}
              onChange={(val) => handleSettingChange("allowReactions", val)}
            />
          </div>

          {/* Allow Raise Hand */}
          <div className="flex items-center justify-between py-2 border-t border-white/5">
            <div>
              <h4 className="text-white font-medium text-sm">
                Allow Raise Hand
              </h4>
              <p className="text-gray-500 text-xs mt-0.5">
                Participants can raise their hand to request attention
              </p>
            </div>
            <ToggleSwitch
              enabled={roomSettings.allowRaiseHand}
              onChange={(val) => handleSettingChange("allowRaiseHand", val)}
            />
          </div>

          {/* Require Password */}
          <div className="py-2 border-t border-white/5">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-white font-medium text-sm">
                  Require Password
                </h4>
                <p className="text-gray-500 text-xs mt-0.5">
                  Participants will need a password to join
                </p>
              </div>
              <ToggleSwitch
                enabled={roomSettings.requirePassword}
                onChange={(val) =>
                  handleSettingChange("requirePassword", val)
                }
              />
            </div>
            {roomSettings.requirePassword && (
              <div className="mt-3 pl-4">
                <input
                  type="text"
                  value={roomSettings.password}
                  onChange={(e) =>
                    handleSettingChange("password", e.target.value)
                  }
                  placeholder="Enter password"
                  className={`${inputClass} max-w-xs`}
                />
              </div>
            )}
          </div>

          {/* Waiting Room */}
          {user?.plan == "Business" && (
            <div className="flex items-center justify-between py-2 border-t border-white/5">
              <div>
                <h4 className="text-white font-medium text-sm">
                  Waiting Room
                </h4>
                <p className="text-gray-500 text-xs mt-0.5">
                  Participants will wait for host approval before joining
                </p>
              </div>
              <ToggleSwitch
                enabled={roomSettings.hostApprovalRequired}
                onChange={(val) =>
                  handleSettingChange("hostApprovalRequired", val)
                }
              />
            </div>
          )}
        </div>
      </div>

      {/* Advanced Settings (Business Plan) */}
      {user?.plan == "Business" && (
        <div className="bg-white/5 rounded-2xl border border-white/10 p-6 space-y-6">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#6a1fbf]" />
            Advanced Settings
          </h3>

          {/* Exit URL */}
          <div className="py-2">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-white font-medium text-sm flex items-center gap-1.5">
                  <Link2 className="w-4 h-4 text-gray-400" />
                  Exit URL
                </h4>
                <p className="text-gray-500 text-xs mt-0.5 ml-5">
                  Redirect participants to a specific URL when they leave
                </p>
              </div>
              <ToggleSwitch
                enabled={exitUrlEnabled}
                onChange={(val) => {
                  setExitUrlEnabled(val);
                  if (!val) setExitUrl("");
                }}
              />
            </div>
            {exitUrlEnabled && (
              <div className="mt-3 ml-5">
                <input
                  type="url"
                  value={exitUrl}
                  onChange={(e) => setExitUrl(e.target.value)}
                  placeholder="https://example.com"
                  className={`${inputClass} ${
                    exitUrl.trim() && !isValidUrl(exitUrl.trim())
                      ? "!border-red-500"
                      : ""
                  }`}
                />
                {exitUrl.trim() && !isValidUrl(exitUrl.trim()) && (
                  <p className="text-red-400 text-xs mt-1">
                    Please enter a valid URL (e.g., https://example.com)
                  </p>
                )}
                {exitUrl.trim() && isValidUrl(exitUrl.trim()) && (
                  <p className="text-green-400 text-xs mt-1">Valid URL</p>
                )}
              </div>
            )}
          </div>

          {/* Custom Button */}
          <div className="py-2 border-t border-white/5">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-white font-medium text-sm flex items-center gap-1.5">
                  <MousePointer className="w-4 h-4 text-gray-400" />
                  Custom Button
                </h4>
                <p className="text-gray-500 text-xs mt-0.5 ml-5">
                  Add a custom button with a link in the meeting room
                </p>
              </div>
              <ToggleSwitch
                enabled={customButtonEnabled}
                onChange={(val) => {
                  setCustomButtonEnabled(val);
                  if (!val) {
                    setCustomButtonText("");
                    setCustomButtonUrl("");
                  }
                }}
              />
            </div>
            {customButtonEnabled && (
              <div className="mt-3 ml-5 space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Button Name
                  </label>
                  <input
                    type="text"
                    value={customButtonText}
                    onChange={(e) => setCustomButtonText(e.target.value)}
                    placeholder="e.g., Visit Our Website"
                    maxLength={50}
                    className={`${inputClass} ${
                      customButtonText.trim() &&
                      customButtonText.trim().length < 3
                        ? "!border-red-500"
                        : ""
                    }`}
                  />
                  <p className="text-gray-500 text-xs mt-1">
                    {customButtonText.trim() &&
                    customButtonText.trim().length < 3 ? (
                      <span className="text-red-400">
                        Minimum 3 characters required
                      </span>
                    ) : (
                      `${customButtonText.length}/50 characters`
                    )}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Button URL
                  </label>
                  <input
                    type="url"
                    value={customButtonUrl}
                    onChange={(e) => setCustomButtonUrl(e.target.value)}
                    placeholder="https://example.com"
                    className={`${inputClass} ${
                      customButtonUrl.trim() &&
                      !isValidUrl(customButtonUrl.trim())
                        ? "!border-red-500"
                        : ""
                    }`}
                  />
                  {customButtonUrl.trim() &&
                    !isValidUrl(customButtonUrl.trim()) && (
                      <p className="text-red-400 text-xs mt-1">
                        Please enter a valid URL
                      </p>
                    )}
                  {customButtonUrl.trim() &&
                    isValidUrl(customButtonUrl.trim()) && (
                      <p className="text-green-400 text-xs mt-1">
                        Valid URL
                      </p>
                    )}
                </div>
              </div>
            )}
          </div>

          {/* Waiting Room Text */}
          <div className="py-2 border-t border-white/5">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-white font-medium text-sm flex items-center gap-1.5">
                  <DoorOpen className="w-4 h-4 text-gray-400" />
                  Waiting Room Text
                </h4>
                <p className="text-gray-500 text-xs mt-0.5 ml-5">
                  Customize messages shown to participants in the waiting room
                </p>
              </div>
              <ToggleSwitch
                enabled={waitingRoomTextEnabled}
                onChange={(val) => {
                  setWaitingRoomTextEnabled(val);
                  if (!val) {
                    setWaitingRoomTitle("");
                    setWaitingRoomMessage("");
                    setWaitingRoomSubText("");
                  }
                }}
              />
            </div>
            {waitingRoomTextEnabled && (
              <div className="mt-3 ml-5 space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={waitingRoomTitle}
                    onChange={(e) => setWaitingRoomTitle(e.target.value)}
                    placeholder="e.g., Waiting for Host to Approve"
                    maxLength={100}
                    className={`${inputClass} ${
                      waitingRoomTitle.trim() &&
                      waitingRoomTitle.trim().length < 3
                        ? "!border-red-500"
                        : ""
                    }`}
                  />
                  <p className="text-gray-500 text-xs mt-1">
                    {waitingRoomTitle.trim() &&
                    waitingRoomTitle.trim().length < 3 ? (
                      <span className="text-red-400">
                        Minimum 3 characters required
                      </span>
                    ) : (
                      `${waitingRoomTitle.length}/100 characters`
                    )}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={waitingRoomMessage}
                    onChange={(e) => setWaitingRoomMessage(e.target.value)}
                    placeholder="e.g., Please wait while the host reviews your request to join"
                    maxLength={200}
                    rows={2}
                    className={`${inputClass} resize-none ${
                      waitingRoomMessage.trim() &&
                      waitingRoomMessage.trim().length < 3
                        ? "!border-red-500"
                        : ""
                    }`}
                  />
                  <p className="text-gray-500 text-xs mt-1">
                    {waitingRoomMessage.trim() &&
                    waitingRoomMessage.trim().length < 3 ? (
                      <span className="text-red-400">
                        Minimum 3 characters required
                      </span>
                    ) : (
                      `${waitingRoomMessage.length}/200 characters`
                    )}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Sub Text <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={waitingRoomSubText}
                    onChange={(e) => setWaitingRoomSubText(e.target.value)}
                    placeholder="e.g., You will be automatically admitted once approved"
                    maxLength={150}
                    className={`${inputClass} ${
                      waitingRoomSubText.trim() &&
                      waitingRoomSubText.trim().length < 3
                        ? "!border-red-500"
                        : ""
                    }`}
                  />
                  <p className="text-gray-500 text-xs mt-1">
                    {waitingRoomSubText.trim() &&
                    waitingRoomSubText.trim().length < 3 ? (
                      <span className="text-red-400">
                        Minimum 3 characters required
                      </span>
                    ) : (
                      `${waitingRoomSubText.length}/150 characters`
                    )}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Custom Background */}
      <div className="bg-white/5 rounded-2xl border border-white/10 p-6 space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Image className="w-5 h-5 text-[#6a1fbf]" />
            Custom Background
          </h3>
          <ToggleSwitch
            enabled={customBackgroundEnabled}
            onChange={(val) => {
              setCustomBackgroundEnabled(val);
              if (!val) {
                setBackgroundImage(null);
                setBackgroundImagePreview(null);
                if (fileInputRef.current) {
                  fileInputRef.current.value = "";
                }
              }
            }}
          />
        </div>

        {customBackgroundEnabled && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              {["solid", "gradient", "image"].map((type) => (
                <button
                  key={type}
                  onClick={() => setBackgroundType(type)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors capitalize ${
                    backgroundType === type
                      ? "bg-[#6a1fbf] text-white"
                      : "bg-white/5 text-gray-400 hover:bg-white/10"
                  }`}
                >
                  {type === "solid"
                    ? "Solid Color"
                    : type === "gradient"
                    ? "Gradient"
                    : "Image"}
                </button>
              ))}
            </div>

            {/* Solid Color Picker */}
            {backgroundType === "solid" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Background Color
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={backgroundColor}
                      onChange={(e) => setBackgroundColor(e.target.value)}
                      className="w-12 h-10 rounded border border-white/10 cursor-pointer bg-transparent"
                    />
                    <input
                      type="text"
                      value={backgroundColor}
                      onChange={(e) => setBackgroundColor(e.target.value)}
                      placeholder="#1a1a2e"
                      className={`${inputClass} max-w-[150px]`}
                    />
                    <div
                      className="w-20 h-10 rounded-lg border border-white/10"
                      style={{ backgroundColor }}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Gradient Color Picker */}
            {backgroundType === "gradient" && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      First Color
                    </label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={gradientColor1}
                        onChange={(e) => setGradientColor1(e.target.value)}
                        className="w-12 h-10 rounded border border-white/10 cursor-pointer bg-transparent"
                      />
                      <input
                        type="text"
                        value={gradientColor1}
                        onChange={(e) => setGradientColor1(e.target.value)}
                        placeholder="#1a1a2e"
                        className={`${inputClass} max-w-[150px]`}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Second Color
                    </label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={gradientColor2}
                        onChange={(e) => setGradientColor2(e.target.value)}
                        className="w-12 h-10 rounded border border-white/10 cursor-pointer bg-transparent"
                      />
                      <input
                        type="text"
                        value={gradientColor2}
                        onChange={(e) => setGradientColor2(e.target.value)}
                        placeholder="#615fff"
                        className={`${inputClass} max-w-[150px]`}
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Gradient Direction
                  </label>
                  <select
                    value={gradientDirection}
                    onChange={(e) => setGradientDirection(e.target.value)}
                    className={`${selectClass} w-full max-w-xs`}
                    style={selectArrowStyle}
                  >
                    <option value="to right">Left to Right</option>
                    <option value="to left">Right to Left</option>
                    <option value="to bottom">Top to Bottom</option>
                    <option value="to top">Bottom to Top</option>
                    <option value="to bottom right">Diagonal</option>
                    <option value="to bottom left">Diagonal</option>
                    <option value="to top right">Diagonal</option>
                    <option value="to top left">Diagonal</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Preview
                  </label>
                  <div
                    className="w-full h-20 rounded-lg border border-white/10"
                    style={{
                      background: `linear-gradient(${gradientDirection}, ${gradientColor1}, ${gradientColor2})`,
                    }}
                  />
                </div>
              </div>
            )}

            {/* Image Upload */}
            {backgroundType === "image" && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Background Image
                  </label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        const maxSize = 5 * 1024 * 1024;
                        if (file.size > maxSize) {
                          toast.error("Image size should not exceed 5MB");
                          e.target.value = "";
                          return;
                        }
                        setBackgroundImage(file);
                        const reader = new FileReader();
                        reader.onloadend = () => {
                          setBackgroundImagePreview(reader.result);
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                    className="block w-full text-sm text-gray-400
                      file:mr-4 file:py-2 file:px-4
                      file:rounded-lg file:border-0
                      file:text-sm file:font-semibold
                      file:bg-[#6a1fbf] file:text-white
                      hover:file:bg-[#7B2FCF]
                      file:cursor-pointer cursor-pointer"
                  />
                  <p className="text-gray-500 text-xs mt-1">
                    Maximum file size: 5MB
                  </p>
                  {backgroundImage && (
                    <p className="text-green-400 text-xs mt-1">
                      Selected: {backgroundImage.name} (
                      {(backgroundImage.size / 1024).toFixed(2)} KB)
                    </p>
                  )}
                </div>

                {backgroundImagePreview && (
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Preview
                    </label>
                    <div className="relative w-full h-64 rounded-lg overflow-hidden border border-white/10 bg-white/5">
                      <img
                        src={backgroundImagePreview}
                        alt="Background preview"
                        className="w-full h-full object-contain"
                      />
                      <button
                        onClick={() => {
                          setBackgroundImage(null);
                          setBackgroundImagePreview(null);
                          if (fileInputRef.current) {
                            fileInputRef.current.value = "";
                          }
                        }}
                        className="absolute top-2 right-2 p-2 bg-red-600 rounded-lg hover:bg-red-700 transition-colors flex items-center gap-1"
                      >
                        <X size={16} />
                        <span className="text-sm">Remove</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* YouTube Streaming (Live Stream only) */}
      {roomType === "Live Stream" && (
        <div className="bg-white/5 border border-white/10 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">
            Streaming Destinations
          </h3>
          <div className="border border-white/10 rounded-lg p-4 bg-white/5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded bg-red-600 flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                </div>
                <span className="text-sm font-medium text-white">YouTube</span>
              </div>
              <button
                type="button"
                onClick={() => setYoutubeEnabled(!youtubeEnabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  youtubeEnabled ? "bg-red-600" : "bg-white/10"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    youtubeEnabled ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>
            {youtubeEnabled && (
              <div className="space-y-2 mt-3">
                <input
                  type="text"
                  placeholder="RTMP URL (e.g., rtmp://a.rtmp.youtube.com/live2)"
                  value={youtubeUrl}
                  onChange={(e) => setYoutubeUrl(e.target.value)}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-white/20"
                />
                <input
                  type="password"
                  placeholder="Stream Key"
                  value={youtubeKey}
                  onChange={(e) => setYoutubeKey(e.target.value)}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-white/20"
                />
                <input
                  type="text"
                  placeholder="Broadcast ID (e.g., bM_H9ekdv2Y)"
                  value={youtubeBroadcastId}
                  onChange={(e) => setYoutubeBroadcastId(e.target.value)}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-white/20"
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-end gap-3">
        <button
          onClick={() => navigate("/dashboard")}
          className="px-6 py-3 bg-white/5 border border-white/10 text-gray-300 rounded-lg hover:bg-white/10 transition-colors font-medium"
        >
          Cancel
        </button>
        <button
          onClick={handleUpdateMeeting}
          className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#6a1fbf] to-[#00c853] text-white rounded-lg hover:opacity-90 transition-opacity font-bold"
        >
          <Save className="w-4 h-4" />
          Update {roomType}
        </button>
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
      />
    </div>
  );
}

export default EditScheduleMeeting;
